import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import * as React from 'react';

import { useDeveloperApi } from './api';
import { CopyButton, describeError, errorEntries, mono } from './components';

/** The scope of the import API's keys (backend IMPORT_API_SCOPES; partner_dev is minted only by a claim link). */
const SCOPE = 'tour_operator';

/** A key's raw value, shown this once (create, rotate): the key and its Authorization header, with the
 *  warning. Nothing keeps it: the dialog that shows it drops it when it closes. */
export const ShownOnceKey = ({ rawKey, children }: { rawKey: string; children?: React.ReactNode }) => {
    const header = `Authorization: Bearer ${rawKey}`;
    return (
        <Stack spacing={2}>
            <Alert severity="warning">Copy the key now: it is shown only once. Curated Planet keeps only a fingerprint of it.</Alert>
            {children}
            <TextField
                size="small"
                fullWidth
                value={rawKey}
                InputProps={{ readOnly: true, sx: mono, endAdornment: <InputAdornment position="end"><CopyButton value={rawKey} label="API key" /></InputAdornment> }}
                helperText="Your API key"
            />
            <TextField
                size="small"
                fullWidth
                value={header}
                InputProps={{ readOnly: true, sx: mono, endAdornment: <InputAdornment position="end"><CopyButton value={header} label="HTTP Authorization header" /></InputAdornment> }}
                helperText="HTTP Authorization header"
            />
        </Stack>
    );
};

interface Props {
    open: boolean;
    onClose: () => void;
    /** Called once the key exists (the list behind the dialog is read again). */
    onCreated: () => void;
    /** A franchise parent's selected licensee: the key is that licensee's. */
    licenseeId?: number | null;
}

/** Developer -> API keys -> New key: POST /v2/edit/api_credential/ through the backend's issuance service
 *  (`tosbx_` on a sandbox). The answer's `raw_key` is shown here once and kept nowhere else: Done drops it. */
const NewApiKeyDialog = ({ open, onClose, onCreated, licenseeId }: Props) => {
    const api = useDeveloperApi();
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const [rawKey, setRawKey] = React.useState<string | null>(null);

    // Once the dialog has faded out: the next New key starts empty, and the raw key is gone.
    const reset = () => {
        setName('');
        setDescription('');
        setSaving(false);
        setError(null);
        setRawKey(null);
    };

    const create = async () => {
        setSaving(true);
        setError(null);
        try {
            const created = await api.createApiCredential({
                name: name.trim(),
                scope: SCOPE,
                ...(description.trim() ? { description: description.trim() } : {}),
                ...(licenseeId ? { tour_operator: licenseeId } : {}),
            });
            setRawKey(created.raw_key);
            onCreated();
        } catch (e) {
            setError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            // While the key is shown only Done closes, so a stray click cannot lose it; while it is being
            // created nothing does, so the answer cannot land in a closed dialog.
            onClose={rawKey || saving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="new-api-key-title"
            TransitionProps={{ onExited: reset }}
            // Above the app's side menu (zIndex 10000 in layout/main.js).
            sx={{ zIndex: 10002 }}
        >
            <DialogTitle id="new-api-key-title">New API key</DialogTitle>
            {rawKey ? (
                <>
                    <DialogContent>
                        <ShownOnceKey rawKey={rawKey} />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={onClose}>Done</Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogContent>
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                A key for HTTP requests to Curated Planet import API. It is shown once, when it is created.
                            </Typography>
                            <TextField
                                autoFocus
                                required
                                size="small"
                                fullWidth
                                label="Name"
                                value={name}
                                onChange={(ev) => setName(ev.target.value)}
                                inputProps={{ maxLength: 256 }}
                                disabled={saving}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                label="Description"
                                helperText="What the key is for, and who uses it."
                                value={description}
                                onChange={(ev) => setDescription(ev.target.value)}
                                inputProps={{ maxLength: 2000 }}
                                disabled={saving}
                            />
                            {error ? (
                                <Alert severity="error">
                                    {describeError(error)}
                                    {errorEntries(error).map((line) => <div key={line}>{line}</div>)}
                                </Alert>
                            ) : null}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={create}
                            disabled={saving || !name.trim()}
                            startIcon={saving ? <CircularProgress size={14} /> : undefined}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default NewApiKeyDialog;
